import { DioboxEvent, MergeTagReplacement, Plan } from "sharedTypes"
import React, { ReactElement } from "react"
import numeral from "numeral"
import { CURRENCY_OPTIONS } from "utilities/enums"
import {
  format,
  formatDistanceToNow,
  formatDistanceToNowStrict,
  isToday,
  isYesterday,
} from "date-fns"
import { toZonedTime, format as formatWithTZ } from "date-fns-tz"

export const UUID = "([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})"
export const INTEGER = "(\\d+)"

export const slugify = (string) => string
  .toString()
  .trim()
  .toLowerCase()
  .replace(/\s+/g, "-")
  .replace(/[^\w-]+/g, "")
  .replace(/--+/g, "-")
  .replace(/^-+/, "")
  .replace(/-+$/, "")

export const titleize = (string) => string
  .replace(/([^A-Z])([A-Z])/g, "$1 $2") // split cameCase
  .replace(/[_-]+/g, " ") // split snake_case and lisp-case
  .toLowerCase()
  .replace(/(^\w|\b\w)/g, (m) => m.toUpperCase()) // title case words
  .replace(/\s+/g, " ") // collapse repeated whitespace
  .replace(/^\s+|\s+$/, "") // remove leading/trailing whitespace

export const removeProtocolFromUrl = (url) => url.replace(/(^\w+:|^)\/\//, "")

export const hasValidCustomDomain = ({ customDomain, whiteLabel }: DioboxEvent) => (
  whiteLabel && customDomain?.validSigning
)

export const customDomainSender = (event: DioboxEvent, fromMailbox?: string | null) => (
  hasValidCustomDomain(event) && event.emailNotificationsSenderMailbox
    ? `${(fromMailbox ?? event.emailNotificationsSenderMailbox)}@${event.customDomain?.domain}`
    : null
)

export const joinWithBr = (parts) => parts
  .filter(Boolean)
  .reduce(
    (all: (string|ReactElement)[], current: string, index: number) => [...all, React.createElement("br", { key: index }), current],
    [],
  )
  .slice(1)

export const guestFullName = (
  personInfo: { firstName?: string; lastName?: string },
): string => `${personInfo.firstName || ""} ${personInfo.lastName || ""}`.trim()

export const firstName = (fullName: string) => {
  if (!fullName.includes(" ")) {
    return fullName
  }

  return fullName.split(" ")[0]
}
export const lastName = (fullName: string) => {
  if (!fullName.includes(" ")) {
    return ""
  }

  return fullName.split(" ").slice(1).join(" ")
}
export const asNumber = (number: number) => numeral(number).format("0,0")
export const asMoney = (number: number|string, currency:string = "USD", truncate?: boolean) => {
  const symbol = CURRENCY_OPTIONS.find((c) => c.value === currency)?.symbol

  return symbol + numeral(number).format(truncate ? "0,0" : "0,0.00")
}
// Lead Retrieval licenses are always charged in USD server-side, regardless of event currency.
export const asUsd = (amount: number) => `US$${numeral(amount).format("0,0")}`
export const asPercent = (number: number) => numeral(number).format("0%")
export const asDateWithoutTime = (date: string): string => date.split("T")[0]
export const asDate = (date: string|Date, f?: string) => {
  if (!date) { return null }

  return format(typeof date === "string" ? new Date(date) : date, f || "PP, p")
}
/**
 * Converts a real UTC event start date into "fake UTC" for the agenda picker.
 * Agenda times are stored/displayed as UTC where the UTC fields represent event-local time.
 * For example, an event at 7 PM EDT (23:00 UTC) becomes "2025-03-31T19:00:00.000Z"
 * so the picker shows 7 PM.
 */
export const eventStartDateToAgendaUTC = (
  eventStartDate: string,
  eventTimezone: string,
): string | null => {
  if (!eventStartDate) return null
  const d = new Date(eventStartDate)
  if (Number.isNaN(d.getTime())) return null
  const zoned = toZonedTime(d, eventTimezone)
  return new Date(Date.UTC(
    zoned.getFullYear(), zoned.getMonth(), zoned.getDate(),
    zoned.getHours(), zoned.getMinutes(), zoned.getSeconds(),
  )).toISOString()
}

export const asDateWithTZ = (date: string, timezone: string, f?: string) => {
  if (!date) { return null }

  const zonedDate = toZonedTime(new Date(date), timezone)

  return formatWithTZ(zonedDate, f || "PP, p", { timeZone: timezone })
}
export const asDateWithoutTZ = (date: string) => asDate(new Date(Date.parse(`${date} 00:00`) || Date.parse(date)), "PP")
export const asDateDistance = (date: string, strict: boolean = false) => {
  const fn = strict ? formatDistanceToNowStrict : formatDistanceToNow

  return fn(new Date(date), { addSuffix: true })
}
export const generateFormatStringWithYesterdayAndToday = (date: string) => {
  const convertedDate = new Date(date)

  if (isToday(convertedDate)) {
    return "'Today,' h:mm a"
  } if (isYesterday(convertedDate)) {
    return "'Yesterday,' h:mm a"
  }

  return undefined
}
export const isUUID = (value: string) => {
  const regexExp = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi

  return regexExp.test(value)
}
export const stripHtmlTags = (input: string) => input.replace(/<[^>]*>/g, "")
export const replaceMergeTagsInText = (
  text: string, replacements: MergeTagReplacement[],
): string => replacements.reduce((updatedText, { mergeTag, value }) => {
  const pattern = new RegExp(mergeTag, "g")

  return updatedText.replace(pattern, value)
}, text)
export const annualDiscount = (interval: "year" | "month", selectedPlan: Plan) => (interval === "year" ? selectedPlan.monthlyPrice * 12 - selectedPlan.annualPrice : 0)

export const URL_VALIDATOR_REGEX = /^(?:(?:(https?|ftp):)?\/\/)?(?:\S+(?::\S*)?@)?(?:(?:[\w-]+\.)+[a-z]{2,}|localhost|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?::\d+)?(?:[/?#]\S*)?$/i

type NamedItem = {
  id: string | number
  name: string
}

export const generateDuplicateName = (
  originalName: string,
  options: {
    suffix?: string
    existingItems?: NamedItem[]
    excludeId?: string | number
  },
): string => {
  const { suffix = "", existingItems, excludeId } = options

  const suffixPattern = suffix ? `\\s+${suffix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}` : ""
  const pattern = suffix
    ? new RegExp(`^(.+?)${suffixPattern}(?:\\s+(\\d+))?$`, "i")
    : /^(.*?)(\d+)$/

  const match = originalName.match(pattern)
  const baseName = match ? match[1].trim() : originalName
  const startNumber = match && match[2] ? parseInt(match[2], 10) + 1 : 2

  // Skip duplication check if existingItems is not provided or empty
  if (!existingItems || existingItems.length === 0) {
    return suffix ? `${baseName} ${suffix} ${startNumber}` : `${baseName} ${startNumber}`
  }

  const isNumberTaken = (num: number): boolean => existingItems.some((item) => {
    if (excludeId && item.id === excludeId) return false
    const itemMatch = item.name.match(pattern)
    if (!itemMatch) return false

    return itemMatch[1].trim() === baseName
    && (itemMatch[2] ? parseInt(itemMatch[2], 10) : 2) === num
  })

  let copyNumber = startNumber
  while (isNumberTaken(copyNumber)) {
    copyNumber += 1
  }

  return suffix ? `${baseName} ${suffix} ${copyNumber}` : `${baseName} ${copyNumber}`
}
